import type { ActionResult } from '@oysterjs/core/graphql/actions';
import { InlineNotice } from '~/components/inline-notice';

type SaveProblem = { title: string; description?: string; canRetry?: boolean };

// A server error's own text is diagnostics, so the page says only that the save didn't land.
const SAVE_FAILED: SaveProblem = {
  title: "We couldn't save your changes.",
  description: 'Nothing was lost. Check your connection and try again.',
  canRetry: true
};

/** What stopped the last save, or null when it worked or there hasn't been one. */
export const saveProblemFrom = (result: ActionResult<unknown>): SaveProblem | null => {
  const problemCount = result?.validationErrors?.length ?? 0;
  if (problemCount === 1) return { title: 'Fix 1 problem before saving.' };
  if (problemCount > 1) return { title: `Fix ${problemCount} problems before saving.` };
  if (result?.applicationError) return { title: result.applicationError.message };
  if (result?.serverError) return SAVE_FAILED;
  return null;
};

/** Every validation message the last save got for one input, joined. */
export const validationMessageFor = (
  result: ActionResult<unknown>,
  inputField: string
): string | undefined => {
  const messages = (result?.validationErrors ?? [])
    .filter((error) => error.field[0] === inputField)
    .map((error) => error.message);
  return messages.length > 0 ? messages.join(' ') : undefined;
};

export const SaveProblemNotice = ({ problem }: { problem: SaveProblem }) => (
  <InlineNotice tone="error" title={problem.title} description={problem.description} />
);
